import { capitalise } from './stringUtils'
import moment from 'moment'
import qs from 'qs'
import { sortBy, without } from './arrayUtils'
import config from 'constants/config'
import { ExperienceSortTypes } from 'actions/ExperienceActions'
import { AnythingCategory } from 'constants/experience'
import * as ArrayUtils from 'lib/arrayUtils'

export function getExperienceDurationString(experience: App.ExperienceOffer) {
  const from = experience.duration.from
  const to = experience.duration.to
  if (from && to && from !== to) {
    return `From ${moment.duration(from).humanize()} to ${moment.duration(to).humanize()}`
  } else if (from && from === to) {
    return capitalise(moment.duration(to).humanize())
  } else if (!from && to) {
    return `Up to ${moment.duration(to).humanize()}`
  } else if (from && !to) {
    return `From ${moment.duration(from).humanize()}`
  }
  return 'Flexible'
}

export function getCustomerLimitRemaining(experience: App.ExperienceOffer, allTicketOptions: App.ExperienceItemTicket[], ticketCounts: Record<string, number>): number | undefined {
  if (!experience.purchaseLimit?.max || !config.EXPERIENCES_PRUCHASE_CUSTOMER_LIMIT) return

  const totalFromServer = ArrayUtils.sum(allTicketOptions.map(ticket => ticket.customerTicketPurchasedAmount), value => value ?? 0)
  const totalFromCart = ArrayUtils.sum(Object.values(ticketCounts), value => value)

  let newPurchaseLimitRemaining = experience.purchaseLimit?.max - (totalFromServer + totalFromCart)
  if (newPurchaseLimitRemaining < 0) newPurchaseLimitRemaining = 0
  return newPurchaseLimitRemaining
}

interface ExperienceListKeyParams {
  latitude?: number;
  longitude?: number;
  currencyCode?: string;
  categoryCodes?: Array<number | undefined>;
  placeId?: string;
  placeIdToIgnore?: string;
  sortBy?: ExperienceSortTypes;
  priceLte?: number;
  priceGte?: number;
  campaigns?: Array<string>;
  distance?: string;
  from?: string;
  to?: string;
  offerId?: string;
  postPurchaseOnly?: boolean;
  showUnlisted?: boolean;
}

export function getExperienceListKey(
  filters: ExperienceListKeyParams = {},
) {
  const tokens = [
    filters.latitude,
    filters.longitude,
    filters.currencyCode,
    without(filters.categoryCodes ?? [], AnythingCategory.id)?.join(','),
    filters.placeId,
    filters.sortBy,
    filters.priceLte,
    filters.priceGte,
    filters.campaigns?.join(','),
    filters.distance,
    filters.from,
    filters.to,
    filters.postPurchaseOnly,
    filters.showUnlisted,
    filters.offerId,
  ]

  return tokens.join('-')
}

interface DatesKeyParams {
  pickupPointId?: string,
  redemptionLocationId?: string,
}

export function getExperienceDatesKey(experienceId: string, params?: DatesKeyParams) {
  return `${experienceId}-${params?.pickupPointId ?? params?.redemptionLocationId}`
}

interface TimesKeyParams {
  currency: string,
  pickupPointId?: string,
  redemptionLocationId?: string,
  isBuyNowBookLater?: boolean
  isGift?: boolean
}

export function getExperienceTimesKey(experienceId: string, date?: string, params?: TimesKeyParams) {
  return `${experienceId}-${date}-${params?.currency}-${params?.pickupPointId ?? params?.redemptionLocationId}-${params?.isBuyNowBookLater ?? false}-${params?.isGift ?? false}`
}

export function getExperienceTicketPurchaseKey(
  ticket: App.ExperienceItemTicket,
  count: number,
) {
  return `${ticket.id}-${count}`
}

export function getExperienceTravellerFormKey(
  items: Array<App.CartExperienceItem>,
) {
  const allTicketsWithCounts = items.flatMap(item => item.tickets).filter(item => item.count > 0)
  return sortBy(allTicketsWithCounts, item => item.ticket.id).map(item =>
    getExperienceTicketPurchaseKey(item.ticket, item.count),
  ).join('/')
}

export function mapExperienceToBookingView(
  exp: App.ExperienceOffer,
  cartExperienceItem?: App.CartExperienceItem | App.Checkout.ExperienceItem,
  property?: App.OfferProperty | App.BedbankProperty,
): App.ExperienceBookingView {
  const leExclusive = exp.leExclusive && config.BRAND === 'luxuryescapes'

  return {
    id: exp.id,
    primaryCategory: exp.primaryCategory?.name,
    categories: exp.categories,
    location: exp.location.name,
    image: exp.images[0],
    price: exp.price,
    grossPrice: exp.grossPrice,
    title: exp.name,
    originalExperience: exp,
    description: exp.copy.dealDescription,
    ticketOptions: [],
    added: !!cartExperienceItem?.tickets.some((t: App.CartExperienceItemTicket | App.Checkout.ExperienceItemTicket) => t.count > 0),
    leExclusive,
    type: exp.productType,
    discount: exp.discount,
    freeCancellation: exp.cancellationPolicies.length > 0,
    interestingLocations: [
      ...(property ? [{
        id: property.id,
        latitude: property.latitude,
        longitude: property.longitude,
        type: 'property' as const,
        name: `Property: ${property.name}`,
      }] : []),
    ],
    ticketUnitLabel: exp.ticketUnitLabel,
  }
}

export interface QueryStringSearchParams {
  destinationId?: string,
  destinationName?: string,
  experienceId?: string,
  experienceName?: string,
  [key: string]: any;
}

export function replaceExperienceQueryString(params: QueryStringSearchParams) {
  const { destinationId, destinationName, ...rest } = params

  const query = qs.stringify({
    destinationId,
    destinationName,
    ...rest,
  }, { arrayFormat: 'comma' })

  return decodeURI(query).replace(/\s/g, '+')
}
